import { useMemo, useState } from "react";
import { useWindowSize } from "hooks/useWindowSize";
import { MobileCreditNoteRow } from "./MobileCreditNoteRow";
import { useLocation, useParams, useRouteMatch } from "react-router-dom";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";

import { AddCreditNoteModal } from "components/AddCreditNoteModal/AddCreditNoteModal";
import { TableUI } from "components/design/TableUI";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { Search } from "components/DesignSystem/Search/Search";
import Slider from "components/DesignSystem/Slider/Slider";
import { EmptyInvoiceList } from "components/Illustrations/EmptyInvoiceList";
import { LockedUi } from "components/LockedUi/LockedUi";

import { CUSTOMER_INFO_TAB } from "constants/invoicing";

import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { useModal } from "hooks/useModal";
import { useQuery, useUpdateQuery } from "hooks/useQuery";

import { AmountSuperScript } from "components/design/AmountSuperScript";
import ConditionalToolTip from "components/design/conditionalToolTip";
import ToolTip from "components/design/toolTip";
import Tab from "components/DesignSystem/Tab/Tab";
import { Tag } from "components/DesignSystem/Tag/Tag";
import {
  CREDIT_NOTE_STATUS,
  CREDIT_NOTE_STATUS_LABEL,
  CREDIT_NOTE_STATUS_TAG_TYPE,
} from "constants/creditNotes";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { CreditNote, useGetAllCreditNotesQuery } from "store/apis/creditNotes";
import { CreditNoteActions } from "../CreditNotes/CreditNotesAction";
import { CreditNoteSlider } from "../CreditNotes/CreditNotesSlider";

type Props = {
  customerName: string;
  customerId: string;
};
const createCreditNoteColumn = createColumnHelper<CreditNote>();

export const CreditNoteColumns = [
  createCreditNoteColumn.accessor("note_id", {
    header: "Id",
    id: "NOTE_ID",
    size: 15,
    cell: (info) => {
      const value = info.getValue();
      return <div className="t-py-2">{value ? value : "-"}</div>;
    },
    enableSorting: true,
  }),

  createCreditNoteColumn.accessor("date", {
    header: "Creation Date",
    id: "DATE",
    size: 15,
    cell: (info) => {
      const value = info.getValue();

      return (
        <div>
          {value ? (
            <div className="t-flex t-gap-2 t-items-center">
              <ToolTip text={dayjs(value).format("MMM DD, YYYY, hh:mm A")}>
                <span className="t-text-body-sm ">
                  {dayjs(value).format(DISPLAY_DATE)}
                </span>
              </ToolTip>
            </div>
          ) : (
            "-"
          )}
        </div>
      );
    },
    enableSorting: true,
  }),
  createCreditNoteColumn.accessor("description", {
    header: "Description",
    id: "DESCRIPTION",
    size: 25,
    cell: (info) => {
      const value = info.getValue();
      return (
        <span className="t-text-body-sm t-truncate" title={value || "-"}>
          {value || "-"}
        </span>
      );
    },
    enableSorting: false,
  }),

  createCreditNoteColumn.accessor("amount", {
    header: "Credit Amount",
    id: "AMOUNT",
    size: 15,
    cell: (info) => {
      const value = info.getValue();
      return (
        <span className="t-text-body-sm ">
          {value ? <AmountSuperScript amount={value} /> : "-"}
        </span>
      );
    },
    enableSorting: true,
  }),

  createCreditNoteColumn.accessor("status", {
    header: "Status",
    id: "STATUS",
    size: 15,
    cell: (info) => {
      const status = info.getValue();
      const row = info.row.original;

      return (
        <ConditionalToolTip
          condition={status === CREDIT_NOTE_STATUS.PARTIALLY_APPLIED}
          text={`Amount remaining: $${row.remaining_balance}`}
        >
          <span>
            <Tag
              rounded
              size="small"
              tagType={CREDIT_NOTE_STATUS_TAG_TYPE[status]}
              icon={false}
            >
              {CREDIT_NOTE_STATUS_LABEL[status]}
            </Tag>
          </span>
        </ConditionalToolTip>
      );
    },
    enableSorting: false,
  }),

  createCreditNoteColumn.display({
    id: "ACTIONS",
    size: 5,
    cell: ({ row, table }) => {
      const meta = table.options.meta as {
        customerId: string;
        customerName: string;
      };
      return (
        <CreditNoteActions
          creditNote={row.original}
          customerId={meta.customerId}
          customerName={meta.customerName}
        />
      );
    },
  }),
];

export const CreditNotes = ({ customerName }: Props) => {
  const { customerId } = useParams<{ customerId: string }>();
  const { url } = useRouteMatch();
  const { search } = useLocation();

  const group = useCurrentGroupContext();
  const entityId = useCurrentEntityId();

  const query = useQuery();
  const { update } = useUpdateQuery();

  const searchTerm = query.get("search") || "";

  const addCreditNoteModal = useModal();
  const creditNoteSlider = useModal();
  const [creditNoteId, setCreditNoteId] = useState<string>("");
  const { isMobile } = useWindowSize();

  const handleCreditNoteRowClick = (uuid: string) => {
    setCreditNoteId(uuid);
    creditNoteSlider.open();
  };
  const [sorting, setSorting] = useState<
    { desc: boolean; id: "DATE" | "AMOUNT" }[]
  >([]);

  const sortCol = sorting[0]?.id;
  const sortOrder = sorting[0]?.desc ? ("DSC" as const) : ("ASC" as const);

  const {
    data: creditNote,
    isLoading,
    isSuccess,
    isFetching,
  } = useGetAllCreditNotesQuery(
    {
      customerId,
      groupId: group?.uuid!,
      entityId: entityId!,
      searchTerm,
      sortCol,
      sortOrder: sortCol ? sortOrder : undefined,
    },
    {
      skip: !group?.uuid || !entityId || !customerId,
    }
  );

  const creditTableData = useMemo(
    () => creditNote?.customer_credit_debit_notes ?? [],
    [creditNote?.customer_credit_debit_notes]
  );

  const table = useReactTable({
    data: creditTableData,
    columns: CreditNoteColumns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    enableMultiSort: false,
    enableSortingRemoval: false,
    // @ts-ignore
    onSortingChange: setSorting,
    state: {
      sorting,
    },
    meta: { customerId, customerName },
  });

  return (
    <Tab.Content value={CUSTOMER_INFO_TAB.CREDIT_NOTES} className="t-pt-4">
      <Async.Root
        isLoading={isLoading}
        isSuccess={isSuccess}
        isEmpty={creditTableData.length === 0 && !searchTerm}
      >
        <Async.Empty>
          <>
            <div className="t-flex t-justify-end t-pb-4">
              <LockedUi
                feature={WRITE_PERMISSION_KEYS.REVENUE}
                blockedUi={<LockedButton text="Add Credit Note" />}
              >
                <Button
                  customType="primary"
                  onClick={addCreditNoteModal.open}
                  size="small"
                >
                  Add Credit Note
                </Button>
              </LockedUi>
            </div>

            <div className="t-flex t-flex-col t-items-center t-justify-center t-h-72 t-text-text-60 t-gap-3 sm:t-h-auto sm:t-py-8 sm:t-text-center">
              <EmptyInvoiceList height={"100"} width={"100"} />
              <div className="t-flex t-flex-col t-items-center t-gap-5">
                <div className="t-flex t-flex-col t-gap-0.5 t-justify-center t-items-center t-mt-5">
                  <p className="t-m-0 t-text-subtitle-sm">
                    Create your first Credit note for {customerName}
                  </p>
                  <p className="t-m-0 t-text-body-sm">
                    Quickly issue a credit note to adjust invoices, refunds, or
                    returns - keeping your books accurate and customers happy.
                  </p>
                </div>
                <LockedUi
                  feature={WRITE_PERMISSION_KEYS.REVENUE}
                  blockedUi={<LockedButton text="Add Credit Note" />}
                >
                  <Button
                    onClick={addCreditNoteModal.open}
                    size="small"
                    customType="primary"
                  >
                    Add Credit Note
                  </Button>
                </LockedUi>
              </div>
            </div>
          </>
        </Async.Empty>

        <Async.Success>
          <div className="t-flex t-justify-between t-pb-4">
            <Search
              isLoading={isFetching}
              className="t-w-[18%] focus:t-w-3/6 t-transition-all"
              defaultValue={searchTerm}
              onChange={(e) =>
                update({ query: "search", value: e.target.value })
              }
              placeholder="Search"
            />

            <LockedUi
              feature={WRITE_PERMISSION_KEYS.REVENUE}
              blockedUi={<LockedButton text="Add Credit Note" />}
            >
              <Button
                customType="primary"
                onClick={addCreditNoteModal.open}
                size="small"
              >
                Add Credit Note
              </Button>
            </LockedUi>
          </div>

          {creditTableData.length === 0 ? (
            <div className="t-flex t-flex-col t-items-center t-justify-center t-h-72 t-text-text-60 t-gap-3 sm:t-h-auto sm:t-py-8 sm:t-text-center">
              <EmptyInvoiceList />
              <div className="t-flex t-flex-col t-gap-2 t-justify-center t-items-center">
                <p className="t-m-0 t-text-subtitle-sm">
                  No results found for your search
                </p>
                <p className="t-m-0 t-text-body-sm">Try clearing the search.</p>
              </div>
              <Button
                onClick={() => update({ query: "search", value: "" })}
                size="small"
                customType="primary"
              >
                Clear search
              </Button>
            </div>
          ) : (
            <>
              {isMobile && (
                <div className="t-flex t-flex-col">
                  {table.getRowModel().rows.map((row) => (
                    <MobileCreditNoteRow
                      key={row.id}
                      row={row}
                      onRowClick={handleCreditNoteRowClick}
                    />
                  ))}
                </div>
              )}
              {!isMobile && (
                <TableUI
                  table={table}
                  onRowClick={(row) =>
                    handleCreditNoteRowClick(row.original.uuid)
                  }
                  enableSort
                />
              )}
            </>
          )}
        </Async.Success>
      </Async.Root>

      {creditNoteSlider.isOpen && creditNoteId && (
        <Slider.Root
          open={creditNoteSlider.isOpen}
          onOpenChange={creditNoteSlider.close}
        >
          <Slider.Content open={creditNoteSlider.isOpen}>
            <CreditNoteSlider
              creditNoteId={creditNoteId}
              customerId={customerId}
            />
          </Slider.Content>
        </Slider.Root>
      )}

      <AddCreditNoteModal
        isOpen={addCreditNoteModal.isOpen}
        close={addCreditNoteModal.close}
        customerId={customerId}
        entityId={entityId!}
      />
    </Tab.Content>
  );
};
